/**
 * Tiny external store for service-worker update state.
 *
 * The SW registration lives outside React (it is registered once in
 * main.tsx), so components read it through `useSyncExternalStore` instead of
 * a context that would re-render the whole tree.
 */
export interface UpdateState {
  /** A new version finished downloading and is waiting to activate. */
  updateAvailable: boolean
  /** The first version finished installing — the app now works offline. */
  offlineReady: boolean
  /** We are in the middle of applying an update. */
  applying: boolean
  /** Registration failed (unsupported browser, blocked scope, …). */
  registerFailed: boolean
  /** The user asked to update while a transaction was running. */
  deferredByTransaction: boolean
}

const initialState: UpdateState = {
  updateAvailable: false,
  offlineReady: false,
  applying: false,
  registerFailed: false,
  deferredByTransaction: false,
}

let state: UpdateState = { ...initialState }
const listeners = new Set<() => void>()

function emit() {
  listeners.forEach((listener) => listener())
}

export const updateStore = {
  getSnapshot(): UpdateState {
    return state
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
  set(partial: Partial<UpdateState>) {
    state = { ...state, ...partial }
    emit()
  },
  reset() {
    state = { ...initialState }
    emit()
  },
}
